import { inject, Injectable } from '@angular/core';
import { AUTH_SCOPE_TOKEN } from '@itb-monorepo/enums';
import { BehaviorSubject, catchError, finalize, Observable, of, shareReplay, tap, } from 'rxjs';
import { HttpMainService } from '../http-main.service';
import { ListModel, RegisterRequestModel, ResetPasswordRequestModel, SectionResponseModel, UserResponseModel, UtmResponseModel } from '@itb-monorepo/models';
import { UserModifyRequestModel } from '@itb-monorepo/models';
import { CustomCookieService } from '../utility-services/custom-cookie.service';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private readonly httpMainService = inject(HttpMainService);
  private readonly cookieService = inject(CustomCookieService);
  private readonly baseUrl = '/member/';
  private readonly _currentUser$ = new BehaviorSubject<
    UserResponseModel | undefined
  >(undefined);

  readonly currentUser$ = this._currentUser$.asObservable();
  private readonly _isReady$ = new BehaviorSubject<boolean>(false);
  private currentUserRequest$?: Observable<UserResponseModel>;
  private readonly scope = inject(AUTH_SCOPE_TOKEN);
  loading = false;

  get currentUser(): UserResponseModel | undefined {
    return this._currentUser$.value;
  }
  loadCurrentUser(forceRefresh = false): Observable<UserResponseModel> {
    if (!forceRefresh && this.currentUser) {
      return of(this.currentUser);
    }
    if (this.currentUserRequest$) {
      return this.currentUserRequest$;
    }

    this.loading = true;

    this.currentUserRequest$ = this.httpMainService
      .get<UserResponseModel>(`${this.baseUrl}v1/${this.scope}/current`)
      .pipe(
        tap((user: UserResponseModel) => {
          this.setCurrentUser(user);
        }),
        finalize(() => {
          this.loading = false;
          this.currentUserRequest$ = undefined;
        }),
        shareReplay({
          bufferSize: 1,
          refCount: false,
        })
      );

    return this.currentUserRequest$;
  }
  getCurrentUser(forceRefresh = false): Observable<UserResponseModel> {
    return this.loadCurrentUser(forceRefresh);
  }

  fetchCurrentUser(forceRefresh = false): void {
    this.loadCurrentUser(forceRefresh)
      .pipe(
        catchError((error) => {
          console.error('Failed to fetch current user', error);

          if (!this.currentUser) {
            this._isReady$.next(false);
          }

          return of(undefined);
        })
      )
      .subscribe();
  }

  setCurrentUser(user: UserResponseModel): void {
    this._currentUser$.next(user);
    this._isReady$.next(true);

    this.updateCurrentUserCookies(user);
  }

  updateCurrentUserProfile(profile: UserResponseModel['profile']): void {
    const currentUser = this.currentUser;

    if (!currentUser) {
      return;
    }

    this.setCurrentUser({
      ...currentUser,
      profile: {
        ...currentUser.profile,
        ...profile,
      },
    });
  }

  clearCurrentUser(): void {
    this._currentUser$.next(undefined);
    this._isReady$.next(false);
    this.currentUserRequest$ = undefined;
  }

  private updateCurrentUserCookies(user: UserResponseModel): void {
    if (this.scope === 'admins') {
      return;
    }

    if (user.section_slug) {
      this.cookieService.set('user_section', user.section_slug);
    }

    if (user.profile?.status) {
      this.cookieService.set('user_kyc', user.profile.status);
    }
  }

  register(model: RegisterRequestModel): Observable<UserResponseModel> {
    return this.httpMainService.post(`${this.baseUrl}v1/users`, model);
  }

  resetPassword(
    model: ResetPasswordRequestModel
  ): Observable<UserResponseModel> {
    return this.httpMainService.patch(`${this.baseUrl}v1/${this.scope}`, model);
  }

  modifyCurrentUserProfile(
    model: UserModifyRequestModel
  ): Observable<UserResponseModel> {
    return this.httpMainService
      .put(`${this.baseUrl}v1/users/current`, model)
      .pipe(
        tap((user: UserResponseModel) => {
          this.setCurrentUser(user);
        })
      );
  }

  getUtm(): Observable<UtmResponseModel> {
    return this.httpMainService
      .get<UtmResponseModel>(`${this.baseUrl}v1/users/current/utm`)
      .pipe(
        tap((res: UtmResponseModel) => {
          const utmFields: (keyof UtmResponseModel)[] = [
            'campaign',
            'content',
            'term',
            'medium',
            'source',
          ];

          utmFields.forEach((field) => {
            const cookieKey = `utm_${field}`;
            const value = res[field];

            if (typeof value === 'string' && value) {
              this.cookieService.set(cookieKey, value);
            } else {
              this.cookieService.delete(cookieKey);
            }
          });
        })
      );
  }

  getListSection(): Observable<ListModel<SectionResponseModel>> {
    return this.httpMainService.get<ListModel<SectionResponseModel>>(
      `${this.baseUrl}v1/sections`
    );
  }
}
